import React, { useRef, useState } from 'react';

let idCounter = 0;
import { translate } from '../Services/translation';
import constants from '../constants';

const hasNameOrChildren = (link) => link.name || (link.links || []).length > 0;

const getLinkCountClass = (amount) => {
  if (amount > 40) return 'large';
  if (amount > 20) return 'medium';
  return 'small';
};

// Icons extracted for readability
const MenuIcon = () => (
  <svg
    className="header__menu-icon"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.8125 10C17.8125 10.2486 17.7137 10.4871 17.5379 10.6629C17.3621 10.8387 17.1236 10.9375 16.875 10.9375H3.125C2.87636 10.9375 2.6379 10.8387 2.46209 10.6629C2.28627 10.4871 2.1875 10.2486 2.1875 10C2.1875 9.75136 2.28627 9.5129 2.46209 9.33709C2.6379 9.16127 2.87636 9.0625 3.125 9.0625H16.875C17.1236 9.0625 17.3621 9.16127 17.5379 9.33709C17.7137 9.5129 17.8125 9.75136 17.8125 10ZM3.125 5.9375H16.875C17.1236 5.9375 17.3621 5.83873 17.5379 5.66291C17.7137 5.4871 17.8125 5.24864 17.8125 5C17.8125 4.75136 17.7137 4.5129 17.5379 4.33709C17.3621 4.16127 17.1236 4.0625 16.875 4.0625H3.125C2.87636 4.0625 2.6379 4.16127 2.46209 4.33709C2.28627 4.5129 2.1875 4.75136 2.1875 5C2.1875 5.24864 2.28627 5.4871 2.46209 5.66291C2.6379 5.83873 2.87636 5.9375 3.125 5.9375ZM16.875 14.0625H3.125C2.87636 14.0625 2.6379 14.1613 2.46209 14.3371C2.28627 14.5129 2.1875 14.7514 2.1875 15C2.1875 15.2486 2.28627 15.4871 2.46209 15.6629C2.6379 15.8387 2.87636 15.9375 3.125 15.9375H16.875C17.1236 15.9375 17.3621 15.8387 17.5379 15.6629C17.7137 15.4871 17.8125 15.2486 17.8125 15C17.8125 14.7514 17.7137 14.5129 17.5379 14.3371C17.3621 14.1613 17.1236 14.0625 16.875 14.0625Z"
      fill="currentColor"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="header__menu-close-icon"
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 15.5 15.5"
  >
    <path
      d="m.15.3l.07-.08C.49-.05.9-.07,1.2.15l.08.07,6.47,6.47L14.22.22c.29-.29.77-.29,1.06,0,.29.29.29.77,0,1.06l-6.47,6.47,6.47,6.47c.27.27.29.68.07.98l-.07.08c-.27.27-.68.29-.98.07l-.08-.07-6.47-6.47L1.28,15.28c-.29.29-.77.29-1.06,0-.29-.29-.29-.77,0-1.06l6.47-6.47L.22,1.28C-.05,1.01-.07.6.15.3l.07-.08-.07.08Z"
      fill="currentColor"
    />
  </svg>
);

// Image links for category navigation (subLevel 1 only)
const ImageLinks = ({ links, clickedIndex, setClickedIndex, uniqueId }) => {
  const hasImages = links.length > 0 && links[0].imageUrl?.length;
  if (!hasImages) return null;

  return (
    <section className="row large-up-4 main-menu__images">
      {links.map(
        (link, index) =>
          hasNameOrChildren(link) && (
            <div className="column" key={`navimage-${uniqueId}-${index}`}>
              <label
                htmlFor={`main-menu__images-radio${index}${uniqueId}`}
                className={clickedIndex === index ? 'active' : ''}
                onClick={() => setClickedIndex(index)}
              >
                <figure>
                  <a href={link.url}>
                    <img src={link.imageUrl} alt={link.extraInfo} />
                    <figcaption>{link.name}</figcaption>
                  </a>
                </figure>
              </label>
            </div>
          )
      )}
    </section>
  );
};

// Header inside submenu panel with back button and title
const MenuHeader = ({ contentLink, onBack }) => (
  <div className="navbar__menu-header">
    <i className="navbar__icon--caret-left navbar__icon" onClick={onBack} />
    <span
      className="navbar__title"
      onClick={onBack}
      dangerouslySetInnerHTML={{ __html: contentLink.name }}
    />
  </div>
);

// Top-level links shown at bottom of main menu
const TopMenuLinks = () => {
  if (!constants?.topLinkList) return null;

  return (
    <ul className="navbar__menu-links navbar__top-menu-links">
      {constants.topLinkList.map((link, index) => (
        <li className="navbar__item" key={`topItem${index}`}>
          <a className="navbar__link" href={link.href}>
            {link.text}
          </a>
        </li>
      ))}
    </ul>
  );
};

// Navigation link with optional caret for items with children
const NavLink = ({ link, hasChildren, subLevel, onOpenSubmenu }) => {
  const additionClass = link.attributes?.cssValue || '';
  const hasChildrenClass = hasChildren ? 'has-children' : '';

  return (
    <>
      <a
        className={`navbar__link ${hasChildrenClass} ${additionClass}`.trim()}
        href={link.url || '#'}
        dangerouslySetInnerHTML={{ __html: link.name }}
      />
      {hasChildren && subLevel <= 1 && (
        <i
          className="navbar__icon--caret-right navbar__icon navbar__icon--open"
          onClick={onOpenSubmenu}
        />
      )}
    </>
  );
};

// Hamburger button that toggles the main mobile menu
const HamburgerButton = ({ isActive, onClick }) => (
  <a
    className="navbar__link--block navbar__icon--menu navbar__icon"
    onClick={onClick}
    rel="nofollow"
    href="#"
    title={translate('general.menu') || 'menu'}
  >
    {isActive ? <CloseIcon /> : <MenuIcon />}
  </a>
);

// Submenu panel containing header, image links, and child items
const SubmenuPanel = ({
  links,
  contentLink,
  subLevel,
  menuRef,
  onToggle,
  clickedIndex,
  setClickedIndex,
  uniqueId,
}) => {
  const hasImages =
    subLevel === 1 && links.length > 0 && links[0].imageUrl?.length;
  const menuClassName = `navbar__menu navbar__menu--mobile ${
    hasImages ? 'navbar__menu-images' : ''
  }`;
  const linksClassName = `navbar__menu-links sub-level-${subLevel} sub-links-count-${
    links.length
  } ${subLevel === 2 ? getLinkCountClass(links.length) : ''}`.trim();

  return (
    <div className={menuClassName} ref={menuRef}>
      {/* Back button and title (shown for submenus, not main menu) */}
      {contentLink && (
        <MenuHeader contentLink={contentLink} onBack={onToggle} />
      )}

      {/* Category images (subLevel 1 only) */}
      {subLevel === 1 && (
        <ImageLinks
          links={links}
          clickedIndex={clickedIndex}
          setClickedIndex={setClickedIndex}
          uniqueId={uniqueId}
        />
      )}

      {/* Child navigation items */}
      <ul className={linksClassName}>
        {links.map(
          (link, index) =>
            hasNameOrChildren(link) && (
              <li className="navbar__item" key={`navItem${index}`}>
                {subLevel <= 1 && (
                  <NavigationItemMobile
                    links={link.links}
                    contentLink={link}
                    subLevel={subLevel + 1}
                  />
                )}
              </li>
            )
        )}
      </ul>

      {/* Additional top links (main menu only) */}
      {!contentLink && <TopMenuLinks />}
    </div>
  );
};

/**
 * Mobile navigation component that handles both:
 * 1. The hamburger menu button (when contentLink is null, subLevel 0)
 * 2. Individual navigation items with expandable submenus
 *
 * Structure:
 * - subLevel 0: Main menu (hamburger button + top-level items)
 * - subLevel 1: Category submenus (with optional image links)
 * - subLevel 2: Leaf items (no further nesting)
 */
const NavigationItemMobile = ({
  links = [],
  contentLink = null,
  subLevel = 0,
}) => {
  const menuRef = useRef(null);
  const idRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(false);

  // Generate stable unique ID (useId is React 18+, this project uses older React)
  if (idRef.current === null) {
    idRef.current = ++idCounter;
  }
  const uniqueId = idRef.current;

  const isTopLevel = !contentLink;
  const hasChildren = links.length > 0;

  // Close all open menus and reset body class
  const closeAllMenus = () => {
    const $bodyEl = document.querySelector('body');
    if (!$bodyEl) return;

    if ($bodyEl.classList.contains('navbar__mobile-menu--active')) {
      document.querySelectorAll('.navbar__menu--show').forEach((menu) => {
        menu.classList.remove('navbar__menu--show');
      });
      $bodyEl.classList.remove('navbar__mobile-menu--active');
      setIsMenuOpen(false);
      return true;
    }
    return false;
  };

  // Toggle menu visibility
  const toggleMenu = (e) => {
    e.preventDefault();

    if (!menuRef.current) return;

    // For hamburger button: handle body class and close all menus if open
    if (isTopLevel) {
      if (closeAllMenus()) return;
      document.body.classList.add('navbar__mobile-menu--active');
    }

    menuRef.current.classList.toggle('navbar__menu--show');
    setIsMenuOpen(!isMenuOpen);
  };

  // Don't render if there's nothing to show
  if (isTopLevel && !hasChildren) {
    return null;
  }

  return (
    <>
      {/* Quick search placeholder - only at top level */}
      {isTopLevel && <div id="quickSearchNew"></div>}

      {/* Render either hamburger button or nav link */}
      {isTopLevel ? (
        <HamburgerButton isActive={isMenuOpen} onClick={toggleMenu} />
      ) : (
        <NavLink
          link={contentLink}
          hasChildren={hasChildren}
          subLevel={subLevel}
          onOpenSubmenu={toggleMenu}
        />
      )}

      {/* Submenu panel (if has children) */}
      {hasChildren && (
        <SubmenuPanel
          links={links}
          contentLink={contentLink}
          subLevel={subLevel}
          menuRef={menuRef}
          onToggle={toggleMenu}
          clickedIndex={clickedIndex}
          setClickedIndex={setClickedIndex}
          uniqueId={uniqueId}
        />
      )}
    </>
  );
};

export default NavigationItemMobile;
